import { type ChangeEvent } from "react";
import type { DropdownProps } from "react-day-picker";

import { cn } from "@/shared/lib/utils";

import { ColocaSelect } from "./ColocaSelect";

/**
 * Month / year dropdown shared by `ColocaDateField` and `ColocaDateRangeField`.
 *
 * React-day-picker renders these as native `<select>`s, whose option list is painted by the browser and not
 * by us: in dark mode Chromium showed a white list with the OS highlight colour on hover, the one control in
 * the app that ignored the theme. `color-scheme` cannot fix it here — the author `background-color` the
 * calendar needs on the select is what pushes Chromium into painting the list itself.
 *
 * `MonthsDropdown` and `YearsDropdown` both delegate to the `Dropdown` slot, so overriding it once covers
 * month and year, in both calendars, with the same Base UI popup every other Coloca select already uses.
 */
export function DayPickerDropdown({
  options,
  value,
  onChange,
  disabled,
  className,
  "aria-label": ariaLabel,
}: DropdownProps) {
  const items = (options ?? []).map((option) => ({
    value: String(option.value),
    label: option.label,
    disabled: option.disabled,
  }));

  const handleValueChange = (next: string) => {
    // DayPicker's `handleMonthChange` / `handleYearChange` read only `Number(event.target.value)`, so a bare
    // target carries everything they consume — a full synthetic event would be dead weight. This is coupled
    // to react-day-picker 10.x; re-check on a major upgrade.
    onChange?.({ target: { value: next } } as unknown as ChangeEvent<HTMLSelectElement>);
  };

  return (
    <ColocaSelect
      value={String(value ?? "")}
      onValueChange={handleValueChange}
      options={items}
      disabled={disabled}
      size="sm"
      // `w-auto` so each dropdown is only as wide as its own label — the caption row is the tightest
      // horizontal budget in the app, and a full-width trigger would push the navigation out of the calendar.
      className={cn("w-auto gap-1 px-2 font-medium", className)}
      aria-label={ariaLabel}
    />
  );
}
